import React, { useState, useEffect } from 'react';

import Slider from '@mui/material/Slider';
import Button from '@mui/material/Button';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import createStyles from '@mui/styles/createStyles';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress, Divider, Grid } from '@mui/material';

import { LabeledCheckbox, MuiChip } from '..';
import { ModalLayout } from './BigModalLayout';
import { fundRaiserPageAction } from '../../store/actions';
import { SINGLE_OWNER_PAGE, TEAM_PAGE } from '../../constants';
import { filtersState as initialFiltersState } from '../../states';

const toId = (value) => {
    const numberValue = Number(value);
    return value !== '' && value !== null && value !== undefined && !Number.isNaN(numberValue) ? numberValue : value;
};

export function FilterModal({ globalState, open, setOpen, applyFilter }) {
    const classes = useStyles();
    let dispatch = useDispatch();
    const [state, setState] = useState(globalState);
    const [applyFilterLoading, setApplyFilterLoading] = useState(false);
    const [resetFilterLoading, setResetFilterLoading] = useState(false);
    const [goalRangeDisabled, setGoalRangeDisabled] = useState(false);
    const [selectedCurrencySymbol, setSelectedCurrencySymbol] = useState();
    const minMaxRange = useSelector(state => state.fundraiser_page.min_max_range);
    // const locationDetails = useSelector(state => state.fundraiser_page.location_details);
    const campaigns = useSelector(state => state.campaigns.campaigns);
    const occasions = useSelector(state => state.occasions.occasions);
    const countries = useSelector(state => state.countries.countries_with_currencies);
    // const pages = useSelector(state => state.fundraiser_page.fundraiser_pages_data)
    const [showMoreOccasions, setShowMoreOccasions] = useState(false)
    const [showMoreCampaigns, setShowMoreCampaigns] = useState(false)
    const [sliderMinValue, setSliderMinValue] = useState(0)
    const [sliderMaxValue, setSliderMaxValue] = useState(0)
    let marks = [
        {
            value: state.goalRange.min,
            label: `${state.goalRange.min} ${selectedCurrencySymbol}`
        },
        {
            value: state.goalRange.max,
            label: `${state.goalRange.max} ${selectedCurrencySymbol}`
        }
    ]

    const mapCheckBoxes = (name, item) => {
        return <Grid item lg={6} md={6} sm={6} xs={12} key={item.id}>
            <LabeledCheckbox
                name={name}
                label={item.name}
                title={item.name}
                value={item.id}
                sx={{
                    whiteSpace: 'nowrap',
                    '& .MuiFormControlLabel-root': {
                        maxWidth: '250px',
                        '& .MuiTypography-root': {
                            textAlign: 'left',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }
                    }
                }}
                checked={isValueSelected(state[`${name}`], item.id)}
                onChange={handleChange} />
        </Grid>
    }

    const passFiltersToCallback = async (payload) => {
        await applyFilter(payload)
    }

    const resetFilters = async () => {
        setResetFilterLoading(true);
        setSliderMinValue(0);
        setSliderMaxValue(0)
        setState({
            ...initialFiltersState
        });
        await passFiltersToCallback(initialFiltersState)
        setResetFilterLoading(false);
    }

    const applyFilterClick = () => {
        setApplyFilterLoading(true);
        passFiltersToCallback(state);
    }

    const handleChange = e => {
        const { name, value } = e.target;
        if (name == "countries") {
            const id = toId(value);
            const alreadySelected = state.countries.some(countryId => toId(countryId) === id);
            const countries = alreadySelected
                ? state.countries.filter(countryId => toId(countryId) !== id)
                : [...state.countries, id];
            const previousSingle = state.countries.length == 1 ? toId(state.countries[0]) : null;
            const nextSingle = countries.length == 1 ? toId(countries[0]) : null;
            setState({
                ...state,
                countries,
                // a different country gets a fresh slider; a range restored from the URL stays put
                goalRange: previousSingle !== nextSingle
                    ? { min: initialFiltersState.goalRange.min, max: initialFiltersState.goalRange.max }
                    : state.goalRange
            })
        }
        else if (name == "goalRange") {
            setState({
                ...state,
                goalRange: { min: value[0], max: value[1] }
            })
        }
        else if (name == "campaigns" || name == "occasions" || name == "pageType") {
            const id = toId(value);
            if (e.target.checked) {
                setState({
                    ...state,
                    [name]: [...state[`${name}`], id]
                })
            }
            else {
                setState({
                    ...state,
                    [name]: state[`${name}`].filter(arrayValue => toId(arrayValue) !== id)
                })
            }
        }
        // else if (name == "pageType") {
        //     if (!e.target.checked) {
        //         setState({
        //             ...state,
        //             pageType: 0
        //         })
        //     }
        //     else setState({
        //         ...state,
        //         [name]: value
        //     })
        // }
        else setState({
            ...state,
            [name]: value
        })
    }

    const isValueSelected = (stateArray, value) => {
        const currencyExists = stateArray.find(array => array == value)
        if (currencyExists) return true
        return false
    }

    useEffect(() => {
        if (!minMaxRange || !minMaxRange.max_range) {
            if (state.countries.length != 1) setGoalRangeDisabled(true);
            return;
        }
        if (state.countries.length == 1) {
            setGoalRangeDisabled(false)
            setSliderMinValue(minMaxRange.min_range)
            setSliderMaxValue(minMaxRange.max_range)
            setState(prev => {
                if (prev.countries.length != 1) return prev;
                const placeholder = prev.goalRange.min === initialFiltersState.goalRange.min && prev.goalRange.max === initialFiltersState.goalRange.max;
                const inBounds = prev.goalRange.min >= minMaxRange.min_range
                    && prev.goalRange.max <= minMaxRange.max_range
                    && prev.goalRange.min <= prev.goalRange.max;
                // keep a range that came from the URL or the slider; only fill in the untouched default
                if (!placeholder && inBounds) return prev;
                if (prev.goalRange.min === minMaxRange.min_range && prev.goalRange.max === minMaxRange.max_range) return prev;
                return {
                    ...prev,
                    goalRange: { min: minMaxRange.min_range, max: minMaxRange.max_range }
                };
            })
        }
        else setGoalRangeDisabled(true)
    }, [minMaxRange])

    useEffect(() => {
        if (open) {
            !campaigns.length && dispatch(fundRaiserPageAction.getListOfCampaigns());
            !occasions.length && dispatch(fundRaiserPageAction.getListOfOccasions());

            setApplyFilterLoading(false);
            setResetFilterLoading(false);
            !globalState.campaigns.length && setShowMoreCampaigns(false);
            !globalState.occasions.length && setShowMoreOccasions(false);
            setState({
                ...globalState,
                goalRange: { ...globalState.goalRange }
            })
        }
    }, [open])

    useEffect(() => {
        if (!open || !campaigns.length || !globalState.campaigns.length) return;
        const hiddenSelection = globalState.campaigns.some(id => campaigns.findIndex(campaign => campaign.id == id) >= 4);
        if (hiddenSelection) setShowMoreCampaigns(true);
    }, [open, campaigns, globalState.campaigns])

    useEffect(() => {
        if (!open || !occasions.length || !globalState.occasions.length) return;
        const hiddenSelection = globalState.occasions.some(id => occasions.findIndex(occasion => occasion.id == id) >= 4);
        if (hiddenSelection) setShowMoreOccasions(true);
    }, [open, occasions, globalState.occasions])

    useEffect(() => {
        !countries && dispatch(fundRaiserPageAction.getListOfCountriesWithCurrencies());
    }, [])

    useEffect(() => {
        if (state.countries.length == 1) {
            dispatch(fundRaiserPageAction.getMinMaxGoalRange(state.countries[0]));
        }
        else {
            setSliderMinValue(0);
            setSliderMaxValue(0);
            setGoalRangeDisabled(true);
        }
    }, [state.countries])

    useEffect(() => {
        if (state.countries.length == 1 && Array.isArray(countries)) {
            const selectedCountry = countries.find(country => country.id == state.countries[0])
            if (selectedCountry) setSelectedCurrencySymbol(selectedCountry.symbol_left)
        }
    }, [state.countries, countries])

    useEffect(() => {
        setState(prev => {
            let nextCount = 0;
            if (prev.countries.length > 0) nextCount++
            if (prev.campaigns.length > 0) nextCount++
            if (prev.occasions.length > 0) nextCount++
            if (prev.pageType.length > 0) nextCount++
            if ((Number(prev.goalRange.min) !== Number(sliderMinValue) ||
                Number(prev.goalRange.max) !== Number(sliderMaxValue)) &&
                prev.countries.length == 1 && sliderMaxValue !== 0)
                nextCount++
            if (prev.selectedFiltersCount === nextCount) return prev;
            return {
                ...prev,
                selectedFiltersCount: nextCount
            };
        })

    }, [state.countries, state.campaigns, state.occasions, state.pageType, state.goalRange, sliderMinValue, sliderMaxValue])

    return (
        <ModalLayout
            open={open}
            title="Filters"
            setOpen={setOpen}
            leftButtonLabel={"Clear all"}
            disabledLeft={applyFilterLoading || globalState.selectedFiltersCount == 0}
            onClickLeft={resetFilters}
            leftButtonLoading={resetFilterLoading}
            rightButtonLabel={"Apply"}
            disabledRight={state.selectedFiltersCount == 0 || resetFilterLoading}
            onClickRight={applyFilterClick}
            loadingRight={applyFilterLoading}
        >
            <Grid container className={classes.body} spacing={5}>
                <Grid item xs={12} className="MainItems">
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography className={classes.sectionHeading}>Type of fundraiser</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <LabeledCheckbox
                                name="pageType"
                                label={"Team Page"}
                                checked={isValueSelected(state.pageType, TEAM_PAGE)}
                                value={TEAM_PAGE}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <LabeledCheckbox
                                name="pageType"
                                label={"Individual Page"}
                                checked={isValueSelected(state.pageType, SINGLE_OWNER_PAGE)}
                                value={SINGLE_OWNER_PAGE}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Divider />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} className="MainItems">
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography className={classes.sectionHeading}>Currencies</Typography>
                        </Grid>
                        <Grid item xs={12} textAlign="center">
                            {countries ? <Grid container spacing={2}>
                                {
                                    countries.map(currency => {
                                        return <Grid item key={currency.id}>
                                            <MuiChip
                                                name="countries"
                                                value={currency.id}
                                                label={currency.name + ` (${currency.currency_code})`}
                                                variant={isValueSelected(state.countries, currency.id) ? 'filled' : 'outlined'}
                                                onClick={handleChange} />
                                        </Grid>
                                    })
                                }
                            </Grid> : <CircularProgress thickness={2} />}
                        </Grid>
                        <Grid item xs={12}>
                            <Divider />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} className="MainItems GoalRange">
                    <Grid container spacing={2} className={classes.goalRangeContainer}>
                        <Grid item xs={12}>
                            <Typography className={classes.sectionHeading}>Goal Range</Typography>
                        </Grid>
                        <Grid item xs={11} sx={{ paddingLeft: '25px !important' }}>
                            <Slider
                                name="goalRange"
                                sx={{ marginLeft: '5px' }}
                                // valueLabelDisplay="auto"
                                min={sliderMinValue}
                                max={!goalRangeDisabled ? sliderMaxValue : undefined}
                                value={!goalRangeDisabled && [state.goalRange.min, state.goalRange.max]}
                                marks={!goalRangeDisabled && marks}
                                disabled={goalRangeDisabled}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Divider />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} className="MainItems">
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography className={classes.sectionHeading}>Campaigns</Typography>
                        </Grid>
                        <Grid item xs={12} textAlign="center">
                            {campaigns.length ? <Grid container>
                                {
                                    showMoreCampaigns ?
                                        campaigns.map(campaign => {
                                            return mapCheckBoxes("campaigns", campaign)
                                        }) :
                                        campaigns.slice(0, 4).map(campaign => {
                                            return mapCheckBoxes("campaigns", campaign)
                                        })
                                }
                                <Grid item xs={12} textAlign="start">
                                    <Button onClick={() => setShowMoreCampaigns(!showMoreCampaigns)}>{showMoreCampaigns ? 'Show less' : 'Show more'}</Button>
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider />
                                </Grid>
                            </Grid> : <CircularProgress thickness={2} />}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} className="MainItems">
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography className={classes.sectionHeading}>Occasions</Typography>
                        </Grid>
                        <Grid item xs={12} textAlign="center">
                            {occasions.length ? <Grid container>
                                {
                                    showMoreOccasions ?
                                        occasions.map(occasion => {
                                            return mapCheckBoxes("occasions", occasion)
                                        }) :
                                        occasions.slice(0, 4).map(occasion => {
                                            return mapCheckBoxes("occasions", occasion)
                                        })
                                }
                                <Grid item xs={12} textAlign="start">
                                    <Button onClick={() => setShowMoreOccasions(!showMoreOccasions)}>{showMoreOccasions ? 'Show less' : 'Show more'}</Button>
                                </Grid>
                            </Grid> : <CircularProgress thickness={2} />}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </ModalLayout>
    );
}
const useStyles = makeStyles((theme) =>
    createStyles({
        marginRight: {
            marginRight: "10px",
        },
        body: {
            paddingLeft: '20px',
            marginTop: '0px !important',

            '& .MainItems': {
                paddingLeft: '0 !important',

                [theme.breakpoints.down('sm')]: {
                    paddingLeft: '10px !important'
                },
            },

            '& .GoalRange': {
                overflow: 'hidden'
            }
        },
        sectionHeading: {
            fontSize: '22px !important',
            fontWeight: '600 !important'
        },
        goalRange: {
            overflowX: 'hidden',
        },
        goalRangeContainer: {
            width: '100% !important'
        }
    }),
);